import { useEffect, useMemo, useState } from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ComposedChart,
} from 'recharts'
import type { Collaboration } from '@/redux/collaborationsApi'
interface PerformanceChartProps {
  campaigns: Collaboration[]
}
const PerformanceChart = ({ campaigns }: PerformanceChartProps) => {
  // Filter campaigns with metrics and format data for the chart
  const chartData = useMemo(
    () =>
      campaigns
        .filter((campaign) => campaign.metrics && campaign.status !== 'rejected')
        .map((campaign) => {
          // Custom engagement and completion rate based on brand
          let engagement = 15.2 // Default
          let completionRate = 100.0 // Default

          // Set specific values based on brand name
          if (campaign.brand.name === 'SportFuel') {
            engagement = 6.8
            completionRate = 85
          } else if (campaign.brand.name === 'FitGear') {
            engagement = 8.8
            completionRate = 97
          }
          return {
            name: campaign.brand.name,
            views: campaign.metrics?.views || 0,
            engagement: engagement,
            completionRate: completionRate,
          }
        }),
    [campaigns],
  )
  const [animatedData, setAnimatedData] = useState<any[]>([])
  useEffect(() => {
    // Start with empty data and set it once
    setAnimatedData([])

    // Set data with a short delay (no loop)
    const timeoutId = setTimeout(() => {
      setAnimatedData(chartData)
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [chartData])

  // Custom tooltip to format the data points
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-md rounded border border-gray-100">
          <p className="font-medium text-black">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={`item-${index}`} className="text-sm text-black flex items-center gap-2">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{
                  backgroundColor: entry.color,
                }}
              ></span>
              <span>
                {entry.name}:{' '}
                {entry.name === 'Views'
                  ? entry.value.toLocaleString()
                  : entry.name === 'Engagement'
                    ? `${entry.value}%`
                    : `${entry.value}%`}
              </span>
            </p>
          ))}
        </div>
      )
    }
    return null
  }
  return (
    <div className="h-full">
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%" className="animate-fade-in">
          <ComposedChart
            data={animatedData}
            margin={{
              top: 5,
              right: 30,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis
              dataKey="name"
              tick={{
                fontSize: 12,
                fill: '#ffffff',
              }}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              tick={{
                fontSize: 12,
                fill: '#ffffff',
              }}
              label={{
                value: 'Views',
                angle: -90,
                position: 'insideLeft',
                style: {
                  textAnchor: 'middle',
                  fill: '#ffffff',
                },
              }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{
                fontSize: 12,
                fill: '#ffffff',
              }}
              label={{
                value: 'Rate (%)',
                angle: -90,
                position: 'insideRight',
                style: {
                  textAnchor: 'middle',
                  fill: '#ffffff',
                },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                color: '#ffffff',
              }}
              formatter={(value) => (
                <span
                  style={{
                    color: '#ffffff',
                  }}
                  className=""
                >
                  {value}
                </span>
              )}
            />
            <Bar
              yAxisId="left"
              dataKey="views"
              name="Views"
              fill="#AFEEF9"
              radius={[4, 4, 0, 0]}
              animationDuration={1200}
              animationEasing="ease-out"
            />
            <Line
              yAxisId="right"
              dataKey="engagement"
              name="Engagement"
              stroke="#A281ED"
              strokeWidth={3}
              dot={{
                r: 4,
                fill: '#A281ED',
              }}
              activeDot={{
                r: 6,
                fill: '#A281ED',
                stroke: '#000',
              }}
              animationDuration={1200}
              animationEasing="ease-out"
            />
            <Line
              yAxisId="right"
              dataKey="completionRate"
              name="Completion Rate"
              stroke="#F4DB66"
              strokeWidth={3}
              dot={{
                r: 4,
                fill: '#F4DB66',
              }}
              activeDot={{
                r: 6,
                fill: '#F4DB66',
                stroke: '#000',
              }}
              animationDuration={1200}
              animationEasing="ease-out"
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-full flex items-center justify-center text-white">
          No performance data available yet
        </div>
      )}
    </div>
  )
}
export default PerformanceChart
